"use client";

import { type DeepPartialSkipArrayKey, useForm } from "react-hook-form";
import { AnimatePresence, motion } from "motion/react";
import { format } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "@/utils/customToast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TimePicker } from "@/features/calendar/components/create-slot/time-picker";
import {
  type CreateSlotFormData,
  createSlotSchema,
  LessonHost,
  type Req,
} from "@/features/calendar/types/create-slot";
import { Button } from "@/components/ui/button.tsx";
import { updateSlot } from "@/features/calendar/api/Slots.ts";
import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/features/calendar/components/create-slot/createSlotAlert.tsx";
import { getDateFnsLocale, getDateStringLocale } from "@/i18n/getDateFnsLocale.ts";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore.tsx";
import { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler.ts";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";
import { Textarea } from "@/components/ui/textarea";
import GenericLoader from "@/components/GenericLoader.tsx";
import {
  slideUpAnimation,
  staggeredContainerAnimation,
} from "@/features/calendar/utils/slot-creation";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function SlotEditContent() {
  const { t, i18n } = useTranslation(["forms", "common", "timezones", "calendar"]);
  const { slot, setIsOpen, isLoading } = useCalendarModalStore();
  const { calendarData } = useCalendarStore();
  const { setToReload } = useCalendarStore();
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hostingOptions = useHostingOptions();

  const defaultValues = useMemo(() => {
    if (slot && "communicationDetails" in slot) {
      return {
        date: format(slot?.startTimeDateTimeString ?? new Date(), "yyyy-MM-dd"),
        end_date: format(slot?.endTimeDateTimeString ?? new Date(), "yyyy-MM-dd"),
        start_time: format(slot?.startTimeDateTimeString ?? new Date(), "HH:mm"),
        end_time: format(slot?.endTimeDateTimeString ?? new Date(), "HH:mm"),
        lesson_host: slot?.lessonHost as LessonHost,
        communication_details: slot?.communicationDetails,
        overlap_gc: false,
      };
    } else {
      return {
        date: format(new Date(), "yyyy-MM-dd"),
        end_date: format(new Date(), "yyyy-MM-dd"),
        start_time: format(new Date(), "HH:mm"),
        end_time: format(new Date(), "HH:mm"),
        lesson_host: slot?.lessonHost as LessonHost,
        communication_details: "",
        overlap_gc: false,
      };
    }
  }, [isLoading, slot]);

  const form = useForm<CreateSlotFormData>({
    resolver: zodResolver(createSlotSchema(t)),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [slot, defaultValues]);

  async function handleSubmit(data: DeepPartialSkipArrayKey<CreateSlotFormData>) {
    setIsSubmitting(true);
    const requestData: Req = {
      end_time: `${data.end_date} ${data.end_time}`, // Use end_date instead of date
      lesson_host: data.lesson_host ?? LessonHost.GoogleMeets,
      communication_details: data.communication_details,
      overlap_gc: data.overlap_gc ? 1 : 0,
      start_time: `${data.date} ${data.start_time}`,
    };

    slot &&
      updateSlot(requestData, slot.id)
        .then(() => {
          toast.success(t("forms:slotEdit.updateSuccess"), {
            description: t("forms:slotEdit.updateSuccessDescription"),
          });
          setIsSubmitting(false);
          setIsOpen(false);
          setToReload(true);
        })
        .catch((error) => {
          console.log(error);
          const errors = error.response.data.errors;

          if (errors) {
            assignApiErrorsToFormFields(errors, form.setError, {
              start_time: "start_time",
              end_time: "end_time",
              lesson_host: "lesson_host",
              date: "date",
              communication_details: "communication_details",
            });
          }
          if (error.response.data.code === "gc_overlap") {
            setAlertDialogOpen(true);
            setIsSubmitting(false);

            return false;
          }
          if (error.response.data.code === "database_overlap") {
            toast.error(t("common:error"), {
              description: t("common:errors.genericErrorTitle"),
            });
            form.setError("end_time", { message: error.response.data.message });
            setIsSubmitting(false);

            return false;
          }
          toast.error(t("forms:common.somethingWentWrong"), {
            description: t("forms:common.tryAgainLater"),
          });
          setIsSubmitting(false);
        });
  }

  if (!slot) {
    return null;
  }
  if (isLoading) {
    return <GenericLoader />;
  }

  return (
    <motion.div
      animate={{ x: 0, opacity: 1 }}
      className="flex h-full flex-col"
      exit={{ x: 100, opacity: 0 }}
      initial={{ x: 100, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <ScrollArea className="h-[calc(100dvh_-_130px)]">
          <AnimatePresence mode="popLayout">
            <motion.div
              key="creation-form"
              animate="animate"
              className="space-y-6 p-6"
              exit="exit"
              initial="initial"
              variants={staggeredContainerAnimation}
            >
              {/* Date and Time Section */}
              <motion.div variants={slideUpAnimation}>
                <Card className="bg-foreground/50 relative z-50 rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 overflow-hidden opacity-5">
                    <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                    <div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
                  </div>

                  <CardContent className="relative z-50 p-6">
                    <div className="mb-6">
                      <FormField
                        control={form.control}
                        name="date"
                        render={({ field }) => (
                          <FormItem className={"space-y-0"}>
                            <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                              {t("common:date")}
                            </FormLabel>
                            <FormControl>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    className="bg-muted/20 hover:bg-muted/30 group flex h-12 w-full items-center justify-between rounded-xl border-none px-4 text-left backdrop-blur-sm transition-colors"
                                    variant="outline"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="bg-muted/30 flex size-8 items-center justify-center rounded-lg backdrop-blur-sm">
                                        <Icon
                                          className="text-accent-foreground"
                                          height="1.25rem"
                                          icon="tabler:calendar"
                                          width="1.25rem"
                                        />
                                      </div>
                                      <span className="text-accent-foreground font-medium">
                                        {new Date(field.value).toLocaleDateString(
                                          getDateStringLocale(i18n.language),
                                        )}
                                      </span>
                                    </div>
                                    <Icon
                                      className="text-muted-foreground"
                                      height="1.25rem"
                                      icon="tabler:chevron-down"
                                      width="1.25rem"
                                    />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                  <Calendar
                                    initialFocus
                                    fromDate={new Date()}
                                    locale={getDateFnsLocale(i18n.language)}
                                    mode="single"
                                    selected={new Date(field.value)}
                                    toDate={new Date(calendarData?.calendarEndsAt ?? new Date())}
                                    weekStartsOn={1}
                                    onSelect={(val) =>
                                      field.onChange(format(val ?? new Date(), "yyyy-MM-dd"))
                                    }
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            <FormMessage className="mt-2" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="start_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                              {t("calendar:templates.startTime")}
                            </FormLabel>
                            <FormControl>
                              <div className="h-12">
                                <TimePicker
                                  className="bg-muted/20 rounded-xl"
                                  pickedDate={new Date(form.watch("date"))}
                                  value={field.value}
                                  onChange={field.onChange}
                                >
                                  <div className="bg-muted/20 hover:bg-muted/30 flex h-12 cursor-pointer items-center gap-3 rounded-xl border-none px-4 backdrop-blur-sm transition-colors">
                                    <Icon
                                      className="text-muted-foreground"
                                      height={"1.25rem"}
                                      icon="tabler:clock"
                                      width={"1.25rem"}
                                    />
                                    <span className="text-accent-foreground font-medium">
                                      {field.value}
                                    </span>
                                  </div>
                                </TimePicker>
                              </div>
                            </FormControl>
                            <FormMessage className="mt-2" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="end_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                              {t("calendar:templates.endTime")}
                            </FormLabel>
                            <FormControl>
                              <div className="h-12">
                                <TimePicker
                                  className="bg-muted/20 rounded-xl"
                                  pickedDate={new Date(form.watch("date"))}
                                  value={field.value}
                                  onChange={field.onChange}
                                >
                                  <div className="bg-muted/20 hover:bg-muted/30 flex h-12 cursor-pointer items-center gap-3 rounded-xl border-none px-4 backdrop-blur-sm transition-colors">
                                    <Icon
                                      className="text-muted-foreground"
                                      height={"1.25rem"}
                                      icon="tabler:clock"
                                      width={"1.25rem"}
                                    />
                                    <span className="text-accent-foreground font-medium">
                                      {field.value}
                                    </span>
                                  </div>
                                </TimePicker>
                              </div>
                            </FormControl>
                            <FormMessage className="mt-2" />
                          </FormItem>
                        )}
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Hosting Method Section */}
              <motion.div variants={slideUpAnimation}>
                <Card className="bg-foreground/50 relative rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 overflow-hidden opacity-5">
                    <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-purple-600" />
                    <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
                  </div>

                  <CardContent className="relative z-50 p-6">
                    <FormField
                      control={form.control}
                      name="lesson_host"
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel className="text-muted-foreground mb-4 block text-base font-semibold">
                              {t("calendar:templates.lessonHost")}
                            </FormLabel>
                            <RadioGroup
                              className="flex flex-col space-y-4"
                              value={field.value}
                              onValueChange={field.onChange}
                            >
                              {hostingOptions.map((option, index) => {
                                const disabled = Boolean(option.disabled);
                                const card = (
                                  <motion.div
                                    key={`host-option-${option.id}`}
                                    animate={{ opacity: 1 }}
                                    className={`relative rounded-xl p-4 backdrop-blur-sm transition-all ${
                                      field.value === option.id
                                        ? "bg-muted/30 shadow-lg"
                                        : disabled
                                          ? "bg-muted/20 cursor-not-allowed opacity-70"
                                          : "bg-muted/20 hover:bg-muted/25 cursor-pointer"
                                    }`}
                                    initial={{ opacity: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    onClick={() => {
                                      if (disabled) {
                                        return;
                                      }
                                      field.onChange(option.id);
                                    }}
                                  >
                                    <FormItem className="m-0 flex items-center space-y-0 space-x-4">
                                      <FormControl>
                                        <RadioGroupItem
                                          className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground"
                                          disabled={disabled}
                                          value={option.id}
                                        />
                                      </FormControl>
                                      <FormLabel className="relative m-0 flex w-full cursor-pointer items-center justify-between text-start font-normal">
                                        <div className="flex items-center gap-4">
                                          <div
                                            className={`flex size-10 items-center justify-center rounded-xl ${disabled ? "opacity-70" : ""}`}
                                          >
                                            {option.icon}
                                          </div>
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={`${disabled ? "text-muted-foreground" : "text-accent-foreground"} text-lg font-semibold`}
                                              >
                                                {option.title}
                                              </span>
                                              {option.id === LessonHost.Ximly && (
                                                <Badge className="bg-accent-foreground text-primary absolute top-2 right-2 rounded-full">
                                                  {t("calendar:templates.recommended")}
                                                </Badge>
                                              )}
                                            </div>
                                            <p className="text-muted-foreground mt-1 text-sm">
                                              {option.description}
                                            </p>
                                            {option.id === LessonHost.Ximly && (
                                              <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t("calendar:templates.additionalFee", {
                                                  fee: "0.36$/hour",
                                                })}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </FormLabel>
                                    </FormItem>
                                    {disabled && (
                                      <div className="bg-background/60 pointer-events-none absolute inset-0 rounded-xl" />
                                    )}
                                  </motion.div>
                                );

                                if (option.hint && disabled) {
                                  return (
                                    <TooltipProvider key={`host-option-${option.id}`}>
                                      <Tooltip delayDuration={200}>
                                        <TooltipTrigger asChild>{card}</TooltipTrigger>
                                        <TooltipContent className="max-w-xs text-left">
                                          <p className="text-accent-foreground text-sm font-medium">
                                            {option.hint.title}
                                          </p>
                                          <p className="text-muted-foreground mt-1 text-xs">
                                            {option.hint.description}
                                          </p>
                                          <a
                                            className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 underline"
                                            href={option.hint.actionHref}
                                            rel="noreferrer"
                                            target="_blank"
                                          >
                                            <span>{option.hint.actionLabel}</span>
                                            <Icon
                                              height={"1rem"}
                                              icon="tabler:arrow-up-right"
                                              width={"1rem"}
                                            />
                                          </a>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                                }

                                return card;
                              })}
                            </RadioGroup>
                            <FormMessage className="mt-2" />
                          </FormItem>
                        );
                      }}
                    />

                    {/* Communication Details (only if other_way is selected) */}
                    <AnimatePresence>
                      {form.watch("lesson_host") === LessonHost.OtherWay && (
                        <motion.div
                          key="communication-details"
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-6"
                          exit={{ opacity: 0, height: 0 }}
                          initial={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <FormField
                            control={form.control}
                            name="communication_details"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                                  {t("forms:hostMethodSelector.communicationDetails")}
                                </FormLabel>
                                <FormControl>
                                  <Textarea
                                    className={`bg-muted/20 hover:bg-muted/30 min-h-[100px] resize-none rounded-xl border-none backdrop-blur-sm transition-colors ${
                                      form.formState.errors.communication_details
                                        ? "border-red-500 ring-red-500"
                                        : ""
                                    }`}
                                    placeholder={t(
                                      "forms:hostMethodSelector.communicationDetailsPlaceholder",
                                    )}
                                    {...field}
                                    maxLength={255}
                                  />
                                </FormControl>
                                <FormMessage className="mt-2" />
                              </FormItem>
                            )}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>

              {/* View Details button */}
              <motion.div variants={slideUpAnimation}>
                <button
                  className="w-full cursor-pointer appearance-none border-none bg-transparent p-0 text-left"
                  type="button"
                  onClick={() => {
                    const { setAction } = useCalendarModalStore.getState();

                    setAction("details");
                  }}
                >
                  <motion.div
                    className="bg-side-accent/50 text-accent-foreground hover:shadow-3xl relative overflow-hidden rounded-2xl border-none p-4 shadow-2xl backdrop-blur-xl transition-all"
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                      <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-blue-400 to-cyan-600" />
                      <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tr from-teal-400 to-blue-500" />
                    </div>

                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-muted/30 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                          <Icon
                            className="text-accent-foreground"
                            height="1.25rem"
                            icon="tabler:info-circle"
                            width="1.25rem"
                          />
                        </div>
                        <div>
                          <p className="text-accent-foreground font-semibold">
                            {t("common:calendar.slot.viewDetails")}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon
                          className="text-accent-foreground transition-transform duration-300 group-hover:translate-x-1"
                          height="1.25rem"
                          icon="tabler:arrow-right"
                          width="1.25rem"
                        />
                      </div>
                    </div>
                  </motion.div>
                </button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </ScrollArea>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="border-border bg-background flex h-16 items-center justify-end border-t px-6 py-1"
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            disabled={isSubmitting || !form.formState.isDirty}
            variant={"foreground"}
            onClick={() => {
              handleSubmit(form.getValues());
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t("common:updating")}
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Icon height="18" icon="tabler:check" width="18" />
                <span>{t("common:save")}</span>
              </div>
            )}
          </Button>
        </motion.div>
      </Form>

      <AlertDialog open={alertDialogOpen} onOpenChange={() => setAlertDialogOpen(!alertDialogOpen)}>
        <AlertDialogContent className={"border-destructive"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("forms:slotEdit.googleCalendarOverlap")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("forms:slotEdit.googleCalendarOverlapDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("forms:common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive/50 hover:destructive/30"
              onClick={() => {
                form.setValue("overlap_gc", true);
                handleSubmit(form.getValues());
              }}
            >
              {t("forms:common.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
